import { makeAutoObservable } from 'mobx';
import { LocalizedValue, createLocalizedValue } from '../../../LocalizedValue';
import { HttpBackend } from '../../../../Services/Http/HttpBackend';
import { createLocalizedEntity, LocalizedEntity } from '../../../LocalizedEntity';
import { Media, createMedia, getMediaSmallest, getMediaMediumOrSmallest, getMediaLargeOrMediumOrSmallest } from '../../../Media/Media';
import { CoachWorkoutPhase, createCoachWorkoutPhase } from './CoachWorkoutPhase';
import { CoachExerciseBlock } from './CoachExerciseBlock';
import dayjs from '../../../../Utils/dayjs';
import { PipelineContext } from '../../PipelineContext';

export type CoachWorkout = LocalizedEntity & {
  scriptId: string;
  type: string;
  phases: CoachWorkoutPhase[];
  image?: Media;
  intensity: string;
  readonly workoutImage: any;
  readonly smallestWorkoutImage: any;
  readonly mediumWorkoutImage: any;
  readonly largeWorkoutImage: any;
  readonly defaultImage: any;
  readonly totalExercises: number;
  readonly duration: number;
  readonly durationFormatted: string;
  readonly durationMinutes: number;
  readonly breakTime: number;
  readonly totalTimeExercising: number;
  readonly isNew: boolean;
  readonly hasExercises: boolean;
  readonly hasPhases: boolean;
  readonly totalSets: number;
  readonly tons: number;
  readonly exerciseBlocks: CoachExerciseBlock[];
  readonly estimatedTotalExerciseTime: number;
  readonly equipmentTypes: string[];
  readonly isBodyWeight: boolean;
  readonly isDurationOnly: boolean;
  calculateCalories(bmr24: number): number;
  save(excludedExerciseBlockIds?: string[]): Promise<CoachWorkout>;
};

export function createCoachWorkout(json?: Partial<CoachWorkout>): CoachWorkout {
  const createPhase = (workout: CoachWorkout, phase: any): CoachWorkoutPhase => {
    return createCoachWorkoutPhase(workout, phase);
  };

  const base = createLocalizedEntity(json);
  const workout: CoachWorkout = makeAutoObservable({
    ...base,
    scriptId: json?.scriptId ?? '',
    type: json?.type ?? 'gym_strength',
    phases: [] as CoachWorkoutPhase[],
    image: json?.image ? createMedia(json.image) : undefined,
    intensity: json?.intensity ?? 'medium',
    name: (json?.name ?? []).map((l) => createLocalizedValue(l)),
    description: (json?.description ?? []).map((l) => createLocalizedValue(l)),

    get workoutImage(): any {
      const mediaUrl = this.image ? getMediaLargeOrMediumOrSmallest(this.image) : null;
      return mediaUrl ? { uri: mediaUrl } : this.defaultImage;
    },

    get smallestWorkoutImage(): any {
      const mediaUrl = this.image ? getMediaSmallest(this.image) : null;
      return mediaUrl ? { uri: mediaUrl } : this.defaultImage;
    },

    get mediumWorkoutImage(): any {
      const mediaUrl = this.image ? getMediaMediumOrSmallest(this.image) : null;
      return mediaUrl ? { uri: mediaUrl } : this.defaultImage;
    },

    get largeWorkoutImage(): any {
      const mediaUrl = this.image ? getMediaLargeOrMediumOrSmallest(this.image) : null;
      return mediaUrl ? { uri: mediaUrl } : this.defaultImage;
    },

    get defaultImage(): any {
      return undefined;
    },

    get totalExercises(): number {
      return this.phases.reduce((total, phase) => total + phase.totalExercises, 0);
    },

    get duration(): number {
      return this.phases.reduce((total, phase) => total + phase.duration, 0);
    },

    get durationFormatted(): string {
      const { duration } = this;
      if (duration > 0) {
        if (duration > 3599999) {
          return dayjs.utc(duration).format('HH:mm:ss');
        }
        return dayjs.utc(duration).format('mm:ss');
      }
      return '00:00';
    },

    get durationMinutes(): number {
      return Math.round(dayjs.duration(this.duration).asMinutes());
    },

    get breakTime(): number {
      return this.phases.reduce((total, phase) => total + phase.breakTime, 0);
    },

    get totalTimeExercising(): number {
      return this.phases.reduce((total, phase) => total + phase.totalTimeExercising, 0);
    },

    get isNew(): boolean {
      return !this.id || this.id.length <= 0;
    },

    get hasExercises(): boolean {
      return this.totalExercises > 0;
    },

    get hasPhases(): boolean {
      return this.phases.length > 0;
    },

    get totalSets(): number {
      return this.phases.reduce((total, p) => total + p.totalSets, 0);
    },

    get tons(): number {
      return this.phases.reduce((total, p) => total + p.tons, 0);
    },

    get exerciseBlocks(): CoachExerciseBlock[] {
      return this.phases.reduce((arr: CoachExerciseBlock[], phase) => arr.concat(phase.exerciseBlocks.slice()), []);
    },

    get estimatedTotalExerciseTime(): number {
      return this.phases.reduce((total, phase) => total + phase.estimatedTotalExerciseTime, 0);
    },

    get equipmentTypes(): string[] {
      return [
        ...new Set<string>(
          this.exerciseBlocks.reduce(
            (arr, viewBlock) => arr.concat(viewBlock.exercise.equipmentTypes.slice()),
            [] as string[],
          ),
        ),
      ];
    },

    get isBodyWeight(): boolean {
      const { equipmentTypes } = this;
      if (equipmentTypes.length <= 2) {
        return equipmentTypes.every((e) => e === 'FREE' || e === 'FREE_OUTDOOR');
      }
      return false;
    },

    get isDurationOnly(): boolean {
      return this.exerciseBlocks.every((block) => {
        return block.sets.every((set) => {
          const hasReps =
            set.values.has('REPETITIONS') || set.values.has('MIN_REPETITIONS') || set.values.has('MAX_REPETITIONS');
          const hasDuration =
            set.values.has('DURATION') || set.values.has('MIN_DURATION') || set.values.has('MAX_DURATION');
          return hasDuration && !hasReps;
        });
      });
    },

    calculateCalories(bmr24: number): number {
      return this.phases.reduce((total, p) => total + p.calculateCalories(bmr24), 0);
    },

    save(excludedExerciseBlockIds: string[] = []): Promise<CoachWorkout> {
      return HttpBackend.post('/coach/workout', this).then(() => this);
    },
  });

  workout.phases = (json?.phases || []).map((p: any) => createPhase(workout, p));

  return workout;
}

export const CoachWorkoutApi = {
  get(id: string): Promise<CoachWorkout> {
    return HttpBackend.get(`/coach/workout/${id}`).then((result) => createCoachWorkout(result));
  },

  generate(workoutTemplateId: string, context?: PipelineContext): Promise<CoachWorkout> {
    return HttpBackend.post(`/coach/workout/generate`, { workoutTemplateId, context }).then((result) =>
      createCoachWorkout(result),
    );
  },
};
